import styled from 'styled-components';
import Link from 'next/link';
import { snakeCase } from 'lodash/fp';

import { HoverDropdownMenu, UnorderedList } from 'shared/components';
import { IFooterBreadcrumb, IFooterData } from 'layout/footer/types/FooterData';
import { sendTrackingEvent } from 'tracking/utils';

const MenuItem = ({ label }: { label: string }) => (
  <MenuItem.LabelWrapper>{label}</MenuItem.LabelWrapper>
);

const DropdownMenu = ({
  menuItems,
  label,
}: {
  menuItems: Array<IFooterBreadcrumb>;
  label: string;
}) => {
  const handleBreadcrumbClick = (name: string) => {
    sendTrackingEvent('NAVIGATION_FOOTER_DRILLDOWN_TAP', {
      footer_title: snakeCase(label),
      footer_subcategory: snakeCase(name),
    });
  };

  return (
    <DropdownMenu.Wrapper>
      {menuItems.map(({ name, slug, id, externalUrl, openInNewTab }) => (
        <DropdownMenu.LinkWrapper key={id}>
          <Link href={externalUrl || `/${slug}`} passHref>
            <a
              tabIndex={0}
              role="link"
              target={openInNewTab ? '_blank' : '_self'}
              rel="noreferrer noopener"
              onClick={() => handleBreadcrumbClick(name)}
              onKeyDown={() => handleBreadcrumbClick(name)}
            >
              {name}
            </a>
          </Link>
        </DropdownMenu.LinkWrapper>
      ))}
    </DropdownMenu.Wrapper>
  );
};

export const FooterNavigationDesktop = ({ footerLinks }: { footerLinks: Array<IFooterData> }) => {
  return (
    <FooterNavigationDesktop.Wrapper>
      {footerLinks.map(({ title, breadcrumbs, id }) => (
        <HoverDropdownMenu
          dropdownMenu={breadcrumbs && <DropdownMenu label={title} menuItems={breadcrumbs} />}
          menuItem={<MenuItem label={title} />}
          style={{ marginRight: '16px' }}
          align="left"
          key={id}
        />
      ))}
    </FooterNavigationDesktop.Wrapper>
  );
};

FooterNavigationDesktop.Wrapper = styled(UnorderedList)`
  display: flex;
`;

MenuItem.LabelWrapper = styled.div`
  display: flex;
  cursor: default;

  :active,
  :focus {
    outline: none;
  }
`;

DropdownMenu.Wrapper = styled(UnorderedList)`
  display: flex;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
`;

DropdownMenu.LinkWrapper = styled.li`
  color: ${({ theme }) => theme.colors.darkGray};
  white-space: nowrap;
  padding: 12px 0 20px 0;

  > a {
    color: ${({ theme }) => theme.navigation.textColor};
    padding: 12px 16px 20px 0;
  }

  :last-child {
    margin-right: 0;
  }

  a:hover {
    color: ${({ theme }) => theme.navigation.textColor};
    text-decoration: underline;
  }
`;
